import * as THREE from 'three';
import { LAMP_CONFIG } from '../config/lampConfig';
// Create a footprint geometry from the base circle
export function createFootprintGeometry(footprintRadius = LAMP_CONFIG.FOOTPRINT_RADIUS, height = LAMP_CONFIG.FOOTPRINT_HEIGHT) {
    // Create a group to hold both geometries
    const group = new THREE.Group();
    // Create the main circular base (90mm diameter)
    const baseShape = new THREE.Shape();
    const segments = 32; // Number of segments for the circle
    const baseRadius = LAMP_CONFIG.BASE_RADIUS; // Use base radius from config
    // Create the base circle
    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * baseRadius;
        const y = Math.sin(angle) * baseRadius;
        if (i === 0) {
            baseShape.moveTo(x, y);
        }
        else {
            baseShape.lineTo(x, y);
        }
    }
    // Create extrusion settings for the base
    const baseExtrudeSettings = {
        steps: 1,
        depth: LAMP_CONFIG.BASE_HEIGHT, // Use base height from config
        bevelEnabled: false
    };
    // Create the base geometry
    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, baseExtrudeSettings);
    baseGeometry.rotateX(-Math.PI / 2);
    // Create the main footprint circle
    const footprintShape = new THREE.Shape();
    // Create the footprint circle
    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * footprintRadius;
        const y = Math.sin(angle) * footprintRadius;
        if (i === 0) {
            footprintShape.moveTo(x, y);
        }
        else {
            footprintShape.lineTo(x, y);
        }
    }
    // Create extrusion settings for the footprint - always use fixed height from config
    const footprintExtrudeSettings = {
        steps: 1,
        depth: LAMP_CONFIG.FOOTPRINT_HEIGHT, // Always use fixed height from config
        bevelEnabled: false
    };
    // Create the footprint geometry
    const footprintGeometry = new THREE.ExtrudeGeometry(footprintShape, footprintExtrudeSettings);
    footprintGeometry.rotateX(-Math.PI / 2);
    // Create meshes for both geometries
    const baseMesh = new THREE.Mesh(baseGeometry);
    const footprintMesh = new THREE.Mesh(footprintGeometry);
    // Add both meshes to the group
    group.add(baseMesh);
    group.add(footprintMesh);
    return group;
}
// Check for clashes between a geometry and a footprint
export function checkFootprintClash(geometry, footprintGeometry) {
    // Create temporary meshes for collision detection
    const tempMesh = new THREE.Mesh(geometry);
    // Get the lamp's vertices and faces
    const positions = geometry.attributes.position.array;
    const indices = geometry.index ? geometry.index.array : null;
    // Define clash geometries
    const lightBaseRadius = LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.RADIUS;
    const lightBaseHeight = LAMP_CONFIG.CLASH_DETECTION.LIGHT_BASE.HEIGHT;
    const lightGlobeRadius = LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.RADIUS;
    const lightGlobeHeight = LAMP_CONFIG.CLASH_DETECTION.LIGHT_GLOBE.HEIGHT;
    // Small epsilon to account for floating point precision
    const EPSILON = 0.001;
    // Check each vertex of the lamp
    for (let i = 0; i < positions.length; i += 3) {
        const vertex = new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]);
        // Skip vertices that are part of the bottom cap (at y=0)
        if (Math.abs(vertex.y) < EPSILON) {
            continue;
        }
        // Check if the vertex is within either clash geometry
        const distanceFromCenter = Math.sqrt(Math.pow(vertex.x, 2) +
            Math.pow(vertex.z, 2));
        // Check light base clash (90mm diameter, 10mm height)
        if (distanceFromCenter <= lightBaseRadius && vertex.y <= lightBaseHeight) {
            return true;
        }
        // Check light globe clash (30mm diameter, 200mm height)
        if (distanceFromCenter <= lightGlobeRadius && vertex.y <= lightGlobeHeight) {
            return true;
        }
    }
    // If we have indices, also check face centers
    if (indices) {
        for (let i = 0; i < indices.length; i += 3) {
            const v1 = new THREE.Vector3(positions[indices[i] * 3], positions[indices[i] * 3 + 1], positions[indices[i] * 3 + 2]);
            const v2 = new THREE.Vector3(positions[indices[i + 1] * 3], positions[indices[i + 1] * 3 + 1], positions[indices[i + 1] * 3 + 2]);
            const v3 = new THREE.Vector3(positions[indices[i + 2] * 3], positions[indices[i + 2] * 3 + 1], positions[indices[i + 2] * 3 + 2]);
            // Skip faces that are part of the bottom cap
            if (Math.abs(v1.y) < EPSILON && Math.abs(v2.y) < EPSILON && Math.abs(v3.y) < EPSILON) {
                continue;
            }
            // Calculate face center
            const faceCenter = new THREE.Vector3()
                .add(v1)
                .add(v2)
                .add(v3)
                .divideScalar(3);
            // Check if the face center is within either clash geometry
            const distanceFromCenter = Math.sqrt(Math.pow(faceCenter.x, 2) +
                Math.pow(faceCenter.z, 2));
            // Check light base clash (90mm diameter, 10mm height)
            if (distanceFromCenter <= lightBaseRadius && faceCenter.y <= lightBaseHeight) {
                return true;
            }
            // Check light globe clash (30mm diameter, 200mm height)
            if (distanceFromCenter <= lightGlobeRadius && faceCenter.y <= lightGlobeHeight) {
                return true;
            }
        }
    }
    return false;
}
// Create a visual representation of the footprint
export function createFootprintMesh(geometry) {
    // Create materials for base and footprint
    const baseMaterial = new THREE.MeshBasicMaterial({
        color: 0x0088ff, // Light blue for base
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide
    });
    const footprintMaterial = new THREE.MeshBasicMaterial({
        color: 0x0088ff, // Same light blue for footprint
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
    });
    // Apply materials to the meshes in the group
    geometry.children[0].material = baseMaterial; // Base mesh
    geometry.children[1].material = footprintMaterial; // Footprint mesh
    return geometry;
}
